import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
}

// Neutralizes foreign inline styles (font family/size/weight, color, alignment,
// highlight backgrounds) that ride along when copy is pasted into the blurb from
// Word/Docs/other pages, so the text always renders in the theme's blurb style.
// Structural formatting the editor sets — bold, italics, links, lists — is
// preserved. Rendered in the JSX (not via useEffect) so it's present in the SSG
// output, not just after hydration.
const NORMALIZE_CSS = `
section.pageblurb-normalized [data-vibesite-richtext],
section.pageblurb-normalized [data-vibesite-richtext] * {
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
  font-style: inherit !important;
  line-height: inherit !important;
  letter-spacing: inherit !important;
  color: inherit !important;
  background-color: transparent !important;
  text-align: inherit !important;
}
section.pageblurb-normalized [data-vibesite-richtext] strong,
section.pageblurb-normalized [data-vibesite-richtext] b {
  font-weight: 700 !important;
}
section.pageblurb-normalized [data-vibesite-richtext] em,
section.pageblurb-normalized [data-vibesite-richtext] i {
  font-style: italic !important;
}
section.pageblurb-normalized [data-vibesite-richtext] a {
  color: var(--vibe-color-hyperlink) !important;
  text-decoration: var(--vibe-color-link-underline, underline) !important;
}
`;

export function PageBlurb({ blurbField, tokenPrefix, defaultText }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageblurb';
  const textPath = `${prefix}.text`;

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="pageblurb-normalized reveal-fade-up pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <style dangerouslySetInnerHTML={{ __html: NORMALIZE_CSS }} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          as="p"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)]"
        />
      </div>

      {/* Schema-lint anchor for static analysis — hidden at runtime */}
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_long_description"></span>',
      suggestedFields: [
        'property_floorplans_blurb',
        'property_amenities_blurb',
        'property_gallery_blurb',
        'property_neighborhood_blurb',
        'property_virtualtours_blurb',
        'property_contactus_blurb',
        'property_reviews_blurb',
        'property_pets_blurb',
        'property_applynow_blurb',
        'property_residentaccess_blurb',
        'property_scheduletour_blurb',
        'property_interactivemap_blurb',
      ],
    },
  },
};

(PageBlurb as any).contentSchema = contentSchema;

export default PageBlurb;
